/**
 * Which zoom levels a person gets is decided by the ROLE they are acting as —
 * not by anything they have bought. This hook reads the workspace the shell is
 * already in (Personal / an organisation / an aggregator / platform admin) and
 * names the role; `levelsForRole` turns that into the notches on the rail.
 *
 * Every aggregator role — Analyst, Admin, Billing Owner — gets the SAME
 * notches. What differs between them is what fills a level and what they may
 * do there, never the rail itself.
 */
import { useWorkspace } from '../../contexts/WorkspaceContext'
import type { WorkspaceKind } from '../../config/navigation'
import { levelsForRole } from './spineLevels'
import type { SpineLevel, SpineRole } from './spineLevels'

const KIND_ROLE: Record<WorkspaceKind, SpineRole> = {
  personal: 'inventor',
  organisation: 'organisation',
  aggregator: 'aggregator',
  admin: 'platform-admin',
}

/**
 * The role the viewer is acting as right now.
 *
 * Outside a WorkspaceProvider (a page's own unit test, which has no shell)
 * this answers 'inventor' rather than throwing, so a screen lane can render
 * its page in isolation without standing the whole shell up.
 */
export function useSpineRole(): SpineRole {
  let kind: WorkspaceKind = 'personal'
  try {
    // Called unconditionally on every render — the try only catches the
    // "must be used within a WorkspaceProvider" throw that happens AFTER
    // useWorkspace's own useContext has run, so the hook order is identical
    // either way. The lint rule cannot see that; the fallback is deliberate.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    kind = useWorkspace().active.kind
  } catch {
    kind = 'personal'
  }
  return KIND_ROLE[kind] ?? 'inventor'
}

/** The notches this viewer's role may open, closest first. */
export function useDefaultLevels(): SpineLevel[] {
  return levelsForRole(useSpineRole())
}
